import type { MaybeRefOrGetter } from "vue";
import { useConsultantDefaultOrganization } from "~/components/organization/composables/useConsultantDefaultOrganization";
import { useRemuneration } from "~/components/remuneration/composables/useRemuneration";
import { useToast } from "~/composables/useToast";
import type { CompanyRemunerationSettings } from "@asap/shared/schemas/remuneration/company-remuneration-settings";
import type {
  RemunerationTemplate,
  RemunerationTemplateInsertPayload,
} from "@asap/shared/schemas/remuneration/remuneration-template";
import type { Company } from "@asap/shared/schemas/company";
import { mapRemunerationTemplateToProfileInsertPayload } from "~/utils/remuneration-contract-profile";
import { getErrorMessage } from "~/utils/error";
import {
  generateUniqueCopyName,
  getCompanyRemunerationLoadPlan,
  getCompanyRemunerationTemplatesView,
} from "./company-details-remuneration.model";

export const useCompanyDetailsRemuneration = (company: MaybeRefOrGetter<Company>) => {
  const { t } = useI18n();
  const { addToastError } = useToast();
  const remuneration = useRemuneration();
  const { consultantDefaultOrganization, loadConsultantDefaultOrganization } = useConsultantDefaultOrganization();

  const remunerationTemplates = ref<RemunerationTemplate[]>([]);
  const templatesRequestLoading = ref(true);
  const consultantOrganizationResolved = ref(false);
  const remunerationSettings = ref<CompanyRemunerationSettings | null>(null);
  const remunerationSettingsLoading = ref(true);
  const referenceDocumentIds = ref<string[]>([]);
  const referenceDocumentsLoading = ref(true);

  const showCreateModal = ref(false);
  const editTemplate = ref<RemunerationTemplate | undefined>(undefined);
  const duplicateDraft = ref<RemunerationTemplateInsertPayload | undefined>(undefined);
  const deleteTemplate = ref<RemunerationTemplate | undefined>(undefined);
  const showNightHoursModal = ref(false);

  const selectedOrganizationId = computed(() => consultantDefaultOrganization.value?.id ?? undefined);
  const templatesView = computed(() =>
    getCompanyRemunerationTemplatesView({
      consultantOrganizationResolved: consultantOrganizationResolved.value,
      templatesRequestLoading: templatesRequestLoading.value,
      organizationId: selectedOrganizationId.value,
    })
  );
  const templatesLoading = computed(() => templatesView.value.templatesLoading);
  const isConsultantOrganizationMissing = computed(() => templatesView.value.missingOrganization);

  const openCreateModal = () => {
    if (!selectedOrganizationId.value) {
      addToastError({
        title: t("companies.remuneration.createWithoutOrganization.title"),
        description: t("companies.remuneration.createWithoutOrganization.description"),
      });
      return;
    }

    showCreateModal.value = true;
  };

  const loadCompanyRemuneration = (companyId: string) => {
    remunerationSettingsLoading.value = true;
    void (async () => {
      try {
        remunerationSettings.value = await remuneration.getSettings(companyId);
      } catch (error) {
        addToastError({ title: t("companies.remuneration.settings.loadError") }, getErrorMessage(error));
      } finally {
        remunerationSettingsLoading.value = false;
      }
    })();

    referenceDocumentsLoading.value = true;
    void (async () => {
      try {
        const response = await remuneration.getReferenceDocumentIds(companyId);
        referenceDocumentIds.value = response.reference_document_ids;
      } catch (error) {
        addToastError({ title: t("companies.remuneration.referenceDocument.loadError") }, getErrorMessage(error));
      } finally {
        referenceDocumentsLoading.value = false;
      }
    })();
  };

  const loadTemplates = (companyId: string, organizationId: string) => {
    templatesRequestLoading.value = true;
    void (async () => {
      try {
        const result = await remuneration.listTemplates(companyId, { organizationId });
        remunerationTemplates.value = result.items;
      } catch (error) {
        addToastError({ title: t("companies.remuneration.templates.loadError") }, getErrorMessage(error));
      } finally {
        templatesRequestLoading.value = false;
      }
    })();
  };

  const reloadRemuneration = () => {
    const companyId = toValue(company).id;
    loadCompanyRemuneration(companyId);
    const organizationId = selectedOrganizationId.value;
    if (organizationId) {
      loadTemplates(companyId, organizationId);
    }
  };

  const openEditModal = (template: RemunerationTemplate) => {
    editTemplate.value = template;
  };

  const openDuplicateModal = (template: RemunerationTemplate) => {
    const organizationId = selectedOrganizationId.value;
    if (!organizationId) {
      addToastError({
        title: t("companies.remuneration.createWithoutOrganization.title"),
        description: t("companies.remuneration.createWithoutOrganization.description"),
      });
      return;
    }

    const existingNames = remunerationTemplates.value.map((existingTemplate) => existingTemplate.name);
    duplicateDraft.value = {
      company_id: toValue(company).id,
      organization_id: organizationId,
      name: generateUniqueCopyName(existingNames, (copyIndex) =>
        copyIndex === 1
          ? t("companies.remuneration.templates.copyName", { name: template.name })
          : t("companies.remuneration.templates.copyNameNumbered", { name: template.name, n: copyIndex })
      ),
      profile: mapRemunerationTemplateToProfileInsertPayload(template),
    };
  };

  const openDeleteModal = (template: RemunerationTemplate) => {
    deleteTemplate.value = template;
  };

  const onTemplateModalClose = () => {
    showCreateModal.value = false;
    editTemplate.value = undefined;
    duplicateDraft.value = undefined;
  };

  const onTemplateSaved = () => {
    showCreateModal.value = false;
    editTemplate.value = undefined;
    duplicateDraft.value = undefined;
    reloadRemuneration();
  };

  const onTemplateDeleted = () => {
    deleteTemplate.value = undefined;
    reloadRemuneration();
  };

  const onSettingsUpdated = () => {
    showNightHoursModal.value = false;
    reloadRemuneration();
  };

  const setReferenceDocumentIds = (ids: string[]) => {
    referenceDocumentIds.value = ids;
  };

  const onReferenceDocumentUpload = (documentId: string) => {
    setReferenceDocumentIds([documentId]);
  };

  onMounted(async () => {
    try {
      await loadConsultantDefaultOrganization();
    } finally {
      consultantOrganizationResolved.value = true;
      if (!selectedOrganizationId.value) {
        templatesRequestLoading.value = false;
      }
    }
  });

  watch(
    () => toValue(company).id,
    (companyId) => {
      const plan = getCompanyRemunerationLoadPlan({ companyId, organizationId: undefined });
      if (!plan.loadSettings || !companyId) return;
      loadCompanyRemuneration(companyId);
    },
    { immediate: true }
  );

  watch(
    () => [toValue(company).id, selectedOrganizationId.value] as const,
    ([companyId, organizationId]) => {
      const plan = getCompanyRemunerationLoadPlan({ companyId, organizationId });
      if (!plan.loadTemplates || !companyId || !organizationId) return;
      loadTemplates(companyId, organizationId);
    }
  );

  return {
    referenceDocumentIds,
    referenceDocumentsLoading,
    remunerationTemplates,
    templatesLoading,
    isConsultantOrganizationMissing,
    remunerationSettings,
    remunerationSettingsLoading,
    showCreateModal,
    editTemplate,
    duplicateDraft,
    deleteTemplate,
    showNightHoursModal,
    selectedOrganizationId,
    openCreateModal,
    openEditModal,
    openDuplicateModal,
    openDeleteModal,
    onTemplateModalClose,
    onTemplateSaved,
    onTemplateDeleted,
    onSettingsUpdated,
    onReferenceDocumentUpload,
  };
};
